import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {createSelector, Store} from '@ngrx/store';
import {
  ActionCreator,
  LabeledAction,
  TableCardHeaderActionDefinition
} from '@aviary/components/table-card/table-card-header-actions/table-card-header-action.types';
import {MxSelector, MxSelectorOrSimply} from '@aviary/store/store.types';
import {isDefined, isPopulatedArray, isString} from '@aviary/store/common/typing.helpers';
import {alwaysEmptyString, alwaysTrue} from '@aviary/store/common/common.selectors';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {SplitButtonComponent, SplitButtonDefinition} from '@aviary/buttons/split-button/split-button.component';
import {asSelector} from '@aviary/store/store.helpers';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';

@Component({
  selector: 'mx-table-card-header-action',
  template: `
    @if (splitButtonDefinition) {
      <mx-split-button [definition]="splitButtonDefinition"/>
    } @else {
      <mx-filled-button [label]="labelSelector | select" [enabled]="enabled | select:false" [tooltip]="tooltipSelector | select:''"
                        (click)="onClick(clickActionCreator)"/>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectPipe, SplitButtonComponent, FilledButtonComponent]
})
export class TableCardHeaderActionComponent {

  @Input() set definition(definition: TableCardHeaderActionDefinition) {
    this.labelSelector = this.createSelectorFor(definition.label);
    this.clickActionCreator = definition.clickActionCreator;
    this.enabled = definition.enabled ?? alwaysTrue;
    this.tooltipSelector = isDefined(definition.tooltip) ? asSelector(definition.tooltip) : alwaysEmptyString;

    const [firstAdditionalAction, ...remainingAdditionalActions]: LabeledAction[] = definition.additionalActions ?? [];

    this.splitButtonDefinition = isPopulatedArray(definition.additionalActions)
      ? {
        mainAction: {
          label: definition.label,
          enabled: definition.enabled,
          clickActionCreator: definition.clickActionCreator
        },
        additionalActions: [firstAdditionalAction, ...remainingAdditionalActions]
      }
      : undefined;
  }

  public labelSelector: MxSelector<string>;
  public clickActionCreator?: ActionCreator;
  public enabled: MxSelector<boolean>;
  public tooltipSelector: MxSelector<string>;
  public splitButtonDefinition?: SplitButtonDefinition;

  private readonly store: Store = inject(Store);

  public onClick(actionCreator?: ActionCreator): void {
    if (actionCreator) this.store.dispatch(actionCreator());
  }

  private createSelectorFor(definitionLabel: MxSelectorOrSimply<string>): MxSelector<string> {
    return isString(definitionLabel) ? createSelector(() => definitionLabel) : definitionLabel;
  }
}
